import React, { useEffect, useState, useMemo } from 'react';
import { Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import PropTypes from 'prop-types';

import DefaultApi from '../../Api/DefaultApi';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
} from '../../Components/ReactDataGridProps';

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'month_amount':
    case 'have_users':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'company_name':
    case 'db_name':
    case 'package_name':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function Companies({ companies, getAllCompanies, onSelectCompany }) {
  const [rows, setRows] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(-1);
  const [filters, setFilters] = useState({
    company_name: '',
    db_name: '',
    package_name: '',
    month_amount: '',
    have_users: '',
    enabled: false,
  });
  const [sortColumns, setSortColumns] = useState([]);

  useEffect(() => {
    if (companies) {
      companies.map((company, index) => {
        let month_amount = company.amount['amount_' + company.currency]
          ? company.amount['amount_' + company.currency] * company.have_users +
            ' ' +
            company.currency.toUpperCase()
          : 'Free';
        companies[index]['month_amount'] = month_amount;
        return true;
      });
      setRows(companies);
    }
  }, [companies]);

  const handleCellClick = (rowData) => {
    if (selectedRowId !== rowData.row.id) {
      setSelectedRowId(rowData.row.id);
      onSelectCompany(rowData.row.id);
    }
  };

  const toggleFilter = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const onClickedDelete = () => {
    if (selectedRowId < 0) return;
    setSelectedRowId(-1);
    DefaultApi.deleteCompany(selectedRowId)
      .then((res) => {
        if (res.status === 200) {
          toast.success(res.message);
          getAllCompanies();
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const tablecolumns = useMemo(() => {
    return [
      {
        key: 'id',
        name: 'ID',
        sortable: true,
        width: 40,
      },
      {
        key: 'company_name',
        name: 'Company Name',
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.company_name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    company_name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'db_name',
        name: 'Database',
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.db_name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    db_name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'package_name',
        name: 'Package',
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.package_name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    package_name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'month_amount',
        name: 'Monthly Amount',
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.month_amount}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    month_amount: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'have_users',
        name: 'Count',
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.have_users}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    have_users: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
    ];
  }, []);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return (
        (filters.company_name
          ? r.company_name
              ?.toLowerCase()
              .includes(filters.company_name.toLowerCase())
          : true) &&
        (filters.db_name
          ? r.db_name?.toLowerCase().includes(filters.db_name.toLowerCase())
          : true) &&
        (filters.package_name
          ? r.package_name
              ?.toLowerCase()
              .includes(filters.package_name.toLowerCase())
          : true) &&
        (filters.month_amount
          ? r.month_amount?.toString().includes(filters.month_amount.toString())
          : true) &&
        (filters.have_users
          ? r.have_users?.toString().includes(filters.have_users.toString())
          : true)
      );
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  return (
    <Grid size={7}>
      <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <IconButton aria-label="Search" onClick={toggleFilter}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={onClickedDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          columns={tablecolumns}
          rows={sortedRows}
          headerRowHeight={filters.enabled ? 70 : 40}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          onCellClick={handleCellClick}
          style={{ maxHeight: window.innerHeight - 140, resize: 'both' }}
        />
      </FilterContext.Provider>
    </Grid>
  );
}

Companies.propTypes = {
  companies: PropTypes.array.isRequired,
  getAllCompanies: PropTypes.func.isRequired,
  onSelectCompany: PropTypes.func.isRequired,
};

export default Companies;
